import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Plus, Edit, Trash2, Star, X, Eye, Upload, Image, Search, RotateCcw, ExternalLink } from 'lucide-react';
import { applicationsService, mediaService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

function ApplicationsPage() {
  const [applications, setApplications] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [formData, setFormData] = useState({
    title: '',
    short_description: '',
    description: '',
    image_url: '',
    hero_image_url: '',
    client_name: '',
    duration: '',
    category: '',
    technologies: [''],
    results: [''],
    benefits: [''],
    demo_url: '',
    is_featured: false,
    display_order: 0
  });

  useEffect(() => {
    loadApplications();
  }, []);

  useEffect(() => {
    filterApplications();
  }, [applications, searchTerm, filterStatus]);

  const loadApplications = async () => {
    try {
      const response = await applicationsService.getAll({ active: 'false' });
      setApplications(response.data.applications || []);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let result = [...applications];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(app =>
        app.title?.toLowerCase().includes(term) ||
        app.client_name?.toLowerCase().includes(term) ||
        app.category?.toLowerCase().includes(term)
      );
    }
    if (filterStatus === 'active') {
      result = result.filter(app => app.is_active);
    } else if (filterStatus === 'inactive') {
      result = result.filter(app => !app.is_active);
    } else if (filterStatus === 'featured') {
      result = result.filter(app => app.is_featured);
    }
    setFilteredApps(result);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
  };

  const openModal = (app = null) => {
    if (app) {
      setEditing(app);
      setFormData({
        title: app.title || '',
        short_description: app.short_description || '',
        description: app.description || '',
        image_url: app.image_url || '',
        hero_image_url: app.hero_image_url || '',
        client_name: app.client_name || '',
        duration: app.duration || '',
        category: app.category || '',
        technologies: app.technologies?.length ? app.technologies : [''],
        results: app.results?.length ? app.results : [''],
        benefits: app.benefits?.length ? app.benefits : [''],
        demo_url: app.demo_url || '',
        is_featured: app.is_featured || false,
        display_order: app.display_order || 0
      });
    } else {
      setEditing(null);
      setFormData({
        title: '',
        short_description: '',
        description: '',
        image_url: '',
        hero_image_url: '',
        client_name: '',
        duration: '',
        category: '',
        technologies: [''],
        results: [''],
        benefits: [''],
        demo_url: '',
        is_featured: false,
        display_order: 0
      });
    }
    setShowModal(true);
  };

  const handleImageUpload = async (e, isHero = false) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop lourde (max 5MB)');
      return;
    }
    if (isHero) {
      setUploadingHero(true);
    } else {
      setUploading(true);
    }
    try {
      const response = await mediaService.uploadImage(file);
      const BASE_URL = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:5000';
      const imageUrl = BASE_URL + response.data.media.url;
      if (isHero) {
        setFormData(prev => ({ ...prev, hero_image_url: imageUrl }));
      } else {
        setFormData(prev => ({ ...prev, image_url: imageUrl }));
      }
      toast.success('Image uploadée');
    } catch (error) {
      console.error('Erreur upload:', error);
      toast.error('Erreur upload');
    } finally {
      setUploading(false);
      setUploadingHero(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      technologies: formData.technologies.filter(t => t.trim() !== ''),
      results: formData.results.filter(r => r.trim() !== ''),
      benefits: formData.benefits.filter(b => b.trim() !== '')
    };
    try {
      if (editing) {
        await applicationsService.update(editing.id, data);
        toast.success('Application mise à jour');
      } else {
        await applicationsService.create(data);
        toast.success('Application créée');
      }
      setShowModal(false);
      loadApplications();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cette application ?')) return;
    try {
      await applicationsService.delete(id);
      toast.success('Application supprimée');
      loadApplications();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (app) => {
    try {
      await applicationsService.update(app.id, { is_active: !app.is_active });
      toast.success('Statut mis à jour');
      loadApplications();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const toggleFeatured = async (app) => {
    try {
      await applicationsService.update(app.id, { is_featured: !app.is_featured });
      toast.success('Mis à jour');
      loadApplications();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const updateArrayField = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-dark-900">Applications</h1>
          <p className="text-dark-500">{filteredApps.length} application(s)</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />
          Nouvelle application
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-dark-100 p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-dark-50 border-0 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 bg-dark-50 border-0 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">Tous</option>
            <option value="active">Actifs</option>
            <option value="inactive">Inactifs</option>
            <option value="featured">Mis en avant</option>
          </select>
          {(searchTerm || filterStatus !== 'all') && (
            <button onClick={resetFilters} className="px-4 py-2 text-dark-600 hover:bg-dark-50 rounded-lg">
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-dark-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-dark-50">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-dark-600">Application</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-dark-600">Client</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-dark-600">Démo</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-dark-600">Statut</th>
              <th className="px-6 py-4 text-right text-sm font-medium text-dark-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dark-100">
            {filteredApps.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center text-dark-500">
                  Aucune application trouvée
                </td>
              </tr>
            ) : (
              filteredApps.map((app) => (
                <tr key={app.id} className="hover:bg-dark-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-dark-100 rounded-lg overflow-hidden flex-shrink-0">
                        {app.image_url ? (
                          <img src={app.image_url} alt={app.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold">
                            {app.title?.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-dark-900 flex items-center gap-2">
                          {app.title}
                          {app.is_featured && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                        </div>
                        <div className="text-sm text-dark-500 truncate max-w-xs">{app.short_description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-dark-600">{app.client_name || '-'}</td>
                  <td className="px-6 py-4">
                    {app.demo_url ? (
                      <a 
                        href={app.demo_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-sm rounded hover:bg-green-200"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Démo
                      </a>
                    ) : (
                      <span className="text-dark-400 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => toggleActive(app)}
                      className={`px-3 py-1 rounded-full text-sm font-medium ${app.is_active ? 'bg-green-100 text-green-700' : 'bg-dark-100 text-dark-500'}`}
                    >
                      {app.is_active ? 'Actif' : 'Inactif'}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => toggleFeatured(app)}
                        className={`p-2 rounded-lg ${app.is_featured ? 'text-yellow-600 bg-yellow-50' : 'text-dark-400 hover:bg-dark-50'}`}
                        title="Mettre en avant"
                      >
                        <Star className={`w-4 h-4 ${app.is_featured ? 'fill-current' : ''}`} />
                      </button>
                      <a href={'/nos-applications/' + app.slug} target="_blank" rel="noopener noreferrer" className="p-2 text-dark-400 hover:bg-dark-50 rounded-lg" title="Voir">
                        <Eye className="w-4 h-4" />
                      </a>
                      <button onClick={() => openModal(app)} className="p-2 text-dark-400 hover:bg-dark-50 hover:text-primary-600 rounded-lg" title="Modifier">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(app.id)} className="p-2 text-dark-400 hover:bg-red-50 hover:text-red-600 rounded-lg" title="Supprimer">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-dark-900/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-dark-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-xl font-semibold">{editing ? 'Modifier l\'application' : 'Nouvelle application'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-dark-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-2">Image principale</label>
                  <div className="border-2 border-dashed border-dark-200 rounded-xl p-4 text-center">
                    {formData.image_url ? (
                      <div className="relative">
                        <img src={formData.image_url} alt="Preview" className="w-full h-40 object-cover rounded-lg" />
                        <button type="button" onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block py-8">
                        <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, false)} className="hidden" disabled={uploading} />
                        {uploading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <div>
                            <Upload className="w-10 h-10 text-dark-400 mx-auto mb-2" />
                            <p className="text-dark-600">Cliquez pour choisir</p>
                            <p className="text-dark-400 text-sm">PNG, JPG max 5MB</p>
                          </div>
                        )}
                      </label>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-2">Image bannière</label>
                  <div className="border-2 border-dashed border-dark-200 rounded-xl p-4 text-center">
                    {formData.hero_image_url ? (
                      <div className="relative">
                        <img src={formData.hero_image_url} alt="Hero" className="w-full h-40 object-cover rounded-lg" />
                        <button type="button" onClick={() => setFormData(prev => ({ ...prev, hero_image_url: '' }))} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block py-8">
                        <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, true)} className="hidden" disabled={uploadingHero} />
                        {uploadingHero ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <div>
                            <Image className="w-10 h-10 text-dark-400 mx-auto mb-2" />
                            <p className="text-dark-600">Image bannière</p>
                            <p className="text-dark-400 text-sm">PNG, JPG max 5MB</p>
                          </div>
                        )}
                      </label>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-dark-700 mb-1">Titre *</label>
                  <input type="text" value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" required />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-dark-700 mb-1">Description courte</label>
                  <textarea value={formData.short_description} onChange={(e) => setFormData(prev => ({ ...prev, short_description: e.target.value }))} className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" rows={2} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1">Client</label>
                  <input type="text" value={formData.client_name} onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))} className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1">Catégorie</label>
                  <input type="text" value={formData.category} onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))} className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Ex: Core Banking" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1">Durée</label>
                  <input type="text" value={formData.duration} onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))} className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Ex: 6 mois" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1">Ordre</label>
                  <input type="number" value={formData.display_order} onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))} className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                </div>
              </div>

              {/* Champ URL Démo */}
              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <label className="block text-sm font-medium text-green-800 mb-1">
                  🚀 URL de la démo (optionnel)
                </label>
                <input 
                  type="url" 
                  value={formData.demo_url} 
                  onChange={(e) => setFormData(prev => ({ ...prev, demo_url: e.target.value }))} 
                  className="w-full px-4 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" 
                  placeholder="Ex: http://45.8.133.211:5000/"
                />
                <p className="text-xs text-green-600 mt-1">
                  Si renseigné, un bouton "Essayer la démo" sera affiché sur la page publique de l'application
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Description complète</label>
                <textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" rows={5} />
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-700 mb-2">Technologies</label>
                {formData.technologies.map((tech, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input type="text" value={tech} onChange={(e) => updateArrayField('technologies', index, e.target.value)} className="flex-1 px-4 py-2 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Ex: React" />
                    {formData.technologies.length > 1 && (
                      <button type="button" onClick={() => removeArrayItem('technologies', index)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => addArrayItem('technologies')} className="text-primary-600 text-sm font-medium">+ Ajouter</button>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-700 mb-2">Résultats</label>
                {formData.results.map((result, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input type="text" value={result} onChange={(e) => updateArrayField('results', index, e.target.value)} className="flex-1 px-4 py-2 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Ex: +25% productivité" />
                    {formData.results.length > 1 && (
                      <button type="button" onClick={() => removeArrayItem('results', index)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => addArrayItem('results')} className="text-primary-600 text-sm font-medium">+ Ajouter</button>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-700 mb-2">Bénéfices</label>
                {formData.benefits.map((benefit, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input type="text" value={benefit} onChange={(e) => updateArrayField('benefits', index, e.target.value)} className="flex-1 px-4 py-2 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Ex: Réduction des coûts" />
                    {formData.benefits.length > 1 && (
                      <button type="button" onClick={() => removeArrayItem('benefits', index)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => addArrayItem('benefits')} className="text-primary-600 text-sm font-medium">+ Ajouter</button>
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_featured" checked={formData.is_featured} onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))} className="w-4 h-4 rounded border-dark-300 text-primary-600 focus:ring-primary-500" />
                <label htmlFor="is_featured" className="text-dark-700">Mettre en avant</label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-dark-100">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-dark-600 hover:bg-dark-50 rounded-lg">Annuler</button>
                <button type="submit" className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">{editing ? 'Enregistrer' : 'Créer'}</button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default ApplicationsPage;